import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { getApiUrl } from '../utils/api';
import { getInstanceSummaryText, getInstanceBadgeColor } from '../utils/instanceStatus';
import ActiveComputePanel from './sidebar/ActiveComputePanel';
import DefaultAvatar from './common/DefaultAvatar';
import { useUserInstances } from '../contexts/UserInstancesContext';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCollapsed?: boolean;
  setIsCollapsed?: (isCollapsed: boolean) => void;
}

interface SidebarItem {
  name: string;
  path: string;
  icon: React.ReactElement;
  badge?: string;
  badgeTone?: 'blue' | 'amber';
}

interface UserInfo {
  name: string;
  email: string;
  picture?: string;
  role?: string;
}

const ADMIN_OPEN_STORAGE_KEY = 'propalgos_admin_nav_open';

export default function DashboardSidebar({ isOpen, setIsOpen, isCollapsed = false, setIsCollapsed }: SidebarProps) {
  const location = useLocation();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  // Shared polling source — the provider owns all /api/compute/instances/user
  // traffic (each call triggers a backend<->AWS sync, so no per-component polls).
  const { activeInstances, refresh: refreshInstances } = useUserInstances();
  const activeSessionCount = activeInstances.length;
  const [adminOpen, setAdminOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(ADMIN_OPEN_STORAGE_KEY) === 'true';
  });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        const decoded = jwtDecode(token) as any;
        setUserInfo({
          name: decoded.name || 'User',
          email: decoded.email || 'user@example.com',
          picture: decoded.picture,
          role: decoded.role || 'user'
        });
      } catch (error) {
        console.error('Error decoding token:', error);
        setUserInfo({ name: 'User', email: 'user@example.com', role: 'user' });
      }
    } else {
      setUserInfo({ name: 'User', email: 'user@example.com', role: 'user' });
    }
  }, []);

  const toggleAdminOpen = () => {
    setAdminOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(ADMIN_OPEN_STORAGE_KEY, next ? 'true' : 'false');
      } catch {
        // localStorage unavailable — state still toggles for the session
      }
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Icons (kept verbatim from the prior sidebar to minimize visual regression)
  // ─────────────────────────────────────────────────────────────────────────

  const icons = {
    home: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    researchFeed: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 11a9 9 0 019 9M4 4a16 16 0 0116 16M6 19a1 1 0 100-2 1 1 0 000 2z" />
      </svg>
    ),
    signalFeeds: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.8L3 21l1.9-3.8A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    apiKeys: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
      </svg>
    ),
    templateLibrary: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 9H9L8 4zm1 14h6" />
      </svg>
    ),
    aiComposer: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l7-7 3 3-7 7-3-3z M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z M2 2l7.586 7.586 M11 11a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    notebooks: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    computeBase: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    sessions: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    datasets: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
    files: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
    myStrategies: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
      </svg>
    ),
    competitions: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    leaderboard: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    billing: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    settings: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    adminDashboard: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    users: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a8.5 8.5 0 00-6.5-6.499" />
      </svg>
    ),
    quotas: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    images: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    costs: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    sampleNotebooks: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    researchEditor: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  };

  const computeIcon = (
    <div className="relative">
      {icons.computeBase}
      {activeSessionCount > 0 && (
        <div className="absolute -top-1 -right-1">
          <span className={`flex h-4 w-4 items-center justify-center rounded-full ${getInstanceBadgeColor(activeInstances)} text-[10px] font-bold text-white`}>
            {activeSessionCount}
          </span>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Sidebar item groups — organized by user intent (Home → Discover → Build →
  // Validate → Arena → Account → Admin)
  // ─────────────────────────────────────────────────────────────────────────

  const homeItems: SidebarItem[] = [
    { name: 'Home', path: '/dashboard', icon: icons.home },
  ];

  const discoverItems: SidebarItem[] = [
    { name: 'Research Feed', path: '/dashboard/research-feed', icon: icons.researchFeed },
    { name: 'Signal Feeds', path: '/dashboard/signal-feeds', icon: icons.signalFeeds },
    { name: 'Template Library', path: '/dashboard/strategy-library', icon: icons.templateLibrary },
    { name: 'Strategy Composer', path: '/dashboard/strategy-composer', icon: icons.aiComposer },
  ];

  const buildItems: SidebarItem[] = [
    { name: 'Notebooks', path: '/dashboard/notebooks', icon: icons.notebooks },
    {
      name: 'Compute',
      path: '/dashboard/compute',
      icon: computeIcon,
      badge: getInstanceSummaryText(activeInstances),
    },
    { name: 'Sessions', path: '/dashboard/sessions', icon: icons.sessions },
    { name: 'Datasets', path: '/dashboard/data', icon: icons.datasets },
    { name: 'Files', path: '/dashboard/storage', icon: icons.files },
  ];

  const validateItems: SidebarItem[] = [
    { name: 'My Strategies', path: '/dashboard/strategies', icon: icons.myStrategies },
  ];

  const arenaItems: SidebarItem[] = [
    { name: 'Competitions', path: '/dashboard/competitions', icon: icons.competitions },
    {
      name: 'Leaderboard',
      path: '/dashboard/leaderboard',
      icon: icons.leaderboard,
      badge: 'Coming soon',
      badgeTone: 'amber',
    },
  ];

  const accountItems: SidebarItem[] = [
    { name: 'Billing', path: '/dashboard/billing', icon: icons.billing },
    { name: 'API Keys', path: '/dashboard/api-keys', icon: icons.apiKeys },
    { name: 'Settings', path: '/dashboard/settings', icon: icons.settings },
  ];

  const adminGroups: { label: string; items: SidebarItem[] }[] = [
    {
      label: 'Platform',
      items: [
        { name: 'Admin Dashboard', path: '/admin/dashboard', icon: icons.adminDashboard },
        { name: 'User Management', path: '/admin/users', icon: icons.users },
        { name: 'Cross-Cloud Costs', path: '/admin/cross-cloud-costs', icon: icons.costs },
      ],
    },
    {
      label: 'Infrastructure',
      items: [
        { name: 'GPU Quotas', path: '/admin/gpu-quotas', icon: icons.quotas },
        { name: 'Image Templates', path: '/admin/ami-builder', icon: icons.images },
      ],
    },
    {
      label: 'Content',
      items: [
        { name: 'Sample Notebooks', path: '/admin/sample-notebooks', icon: icons.sampleNotebooks },
        { name: 'Datasets', path: '/admin/datasets', icon: icons.datasets },
        { name: 'Research Editor', path: '/admin/research', icon: icons.researchEditor },
      ],
    },
  ];

  const flatAdminItems: SidebarItem[] = adminGroups.flatMap((g) => g.items);
  const isAdmin = userInfo?.role === 'admin';

  const SidebarLink = ({ item, onClick }: { item: SidebarItem; onClick?: () => void }) => {
    const isActive = item.path === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(item.path);

    const badgeClasses = item.badgeTone === 'amber'
      ? 'ml-2 flex-shrink-0 bg-amber-500/15 text-amber-300/90 text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap'
      : 'ml-auto bg-blue-500 text-xs text-white px-2 py-1 rounded-full';

    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center text-sm font-medium rounded-lg transition-colors ${
          isActive
            ? 'bg-gray-800 text-white'
            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
        } ${
          isCollapsed
            ? 'justify-center px-3 py-3 mx-1'
            : 'px-4 py-3'
        }`}
        title={isCollapsed ? item.name : undefined}
      >
        <div className={isCollapsed ? 'flex items-center justify-center' : ''}>
          {item.icon}
        </div>
        {!isCollapsed && (
          <>
            <span className="ml-3 truncate min-w-0 flex-1">{item.name}</span>
            {item.badge && <span className={badgeClasses}>{item.badge}</span>}
          </>
        )}
      </Link>
    );
  };

  const GroupHeader = ({ label }: { label: string }) => (
    <div className="px-4 mb-2">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</h3>
    </div>
  );

  const AdminSubgroupHeader = ({ label }: { label: string }) => (
    <div className="px-4 mb-1 mt-3 first:mt-1">
      <h4 className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">{label}</h4>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-screen bg-gray-900 border-r border-gray-800 z-30 transform transition-all duration-300 ease-in-out flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0 md:fixed md:z-30 md:h-screen ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}>

        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-gray-800 flex-shrink-0">
          <Link to="/" className={`flex items-center hover:opacity-80 transition-opacity ${isCollapsed ? 'justify-center w-full' : 'flex-1'}`}>
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            {!isCollapsed && <span className="ml-3 text-xl font-bold text-white">PropAlgos</span>}
          </Link>

          {/* Collapse button (only when expanded) */}
          {setIsCollapsed && !isCollapsed && (
            <button
              onClick={() => setIsCollapsed(true)}
              className="hidden md:flex items-center justify-center w-8 h-8 ml-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Collapse sidebar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {isCollapsed && setIsCollapsed && (
          <div className="hidden md:flex justify-center px-4 py-3 border-b border-gray-800">
            <button
              onClick={() => setIsCollapsed(false)}
              className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
              title="Expand sidebar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 px-4 py-6 overflow-y-auto min-h-0">
          {/* Home (ungrouped) */}
          <div className="space-y-1 mb-6">
            {homeItems.map((item) => (
              <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
            ))}
          </div>

          {/* Discover */}
          <div className="space-y-1 mb-6">
            {!isCollapsed && <GroupHeader label="Discover" />}
            {discoverItems.map((item) => (
              <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
            ))}
          </div>

          {/* Build — Notebooks / Compute (+ ActiveComputePanel inline) / Sessions / Datasets / Files */}
          <div className="space-y-1 mb-6">
            {!isCollapsed && <GroupHeader label="Build" />}
            {buildItems.map((item) => (
              <React.Fragment key={item.path}>
                <SidebarLink item={item} onClick={() => setIsOpen(false)} />
                {item.path === '/dashboard/compute' && activeSessionCount > 0 && (
                  <ActiveComputePanel
                    instances={activeInstances}
                    isCollapsed={isCollapsed}
                    onInstanceTerminated={refreshInstances}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Validate */}
          <div className="space-y-1 mb-6">
            {!isCollapsed && <GroupHeader label="Validate" />}
            {validateItems.map((item) => (
              <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
            ))}
          </div>

          {/* Arena */}
          <div className="space-y-1 mb-6">
            {!isCollapsed && <GroupHeader label="Arena" />}
            {arenaItems.map((item) => (
              <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
            ))}
          </div>

          {/* Account */}
          <div className="space-y-1">
            {!isCollapsed && <GroupHeader label="Account" />}
            {accountItems.map((item) => (
              <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
            ))}
          </div>

          {/* Admin — collapsible disclosure with subgroups when expanded sidebar; flat list when icon-only */}
          {isAdmin && (
            <div className="space-y-1 mt-8">
              {isCollapsed ? (
                flatAdminItems.map((item) => (
                  <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
                ))
              ) : (
                <>
                  <button
                    onClick={toggleAdminOpen}
                    className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-red-400 uppercase tracking-wider hover:text-red-300 transition-colors"
                    aria-expanded={adminOpen}
                  >
                    <span className="flex items-center">
                      <svg
                        className={`w-3 h-3 mr-1.5 transition-transform ${adminOpen ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Admin
                    </span>
                  </button>
                  {adminOpen && (
                    <div className="space-y-1">
                      {adminGroups.map((group) => (
                        <div key={group.label} className="space-y-1">
                          <AdminSubgroupHeader label={group.label} />
                          {group.items.map((item) => (
                            <SidebarLink key={item.path} item={item} onClick={() => setIsOpen(false)} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* User Profile */}
        <div className="border-t border-gray-800 p-4 flex-shrink-0">
          <div className={`flex items-center ${isCollapsed ? 'flex-col space-y-2' : ''}`}>
            {userInfo?.picture ? (
              <img
                src={userInfo.picture}
                alt="Profile"
                className="w-8 h-8 rounded-full"
                title={isCollapsed ? userInfo.name || 'User' : undefined}
              />
            ) : (
              <DefaultAvatar name={userInfo?.name} email={userInfo?.email} size="sm" />
            )}
            {!isCollapsed && (
              <div className="ml-3 flex-1">
                <div className="text-sm font-medium text-white truncate">{userInfo?.name || 'User'}</div>
                <div className="text-xs text-gray-400 truncate">{userInfo?.email || 'user@example.com'}</div>
              </div>
            )}
            <button
              className="text-gray-400 hover:text-white"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('authToken');
                  if (token) {
                    await fetch(`${getApiUrl()}/auth/logout`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                  }
                } catch {
                  // best-effort — proceed with logout regardless
                }
                localStorage.removeItem('authToken');
                window.location.href = '/login';
              }}
              title="Sign out"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
